import { $menu } from '@/views/menu/menu';
// @ts-ignore
import template from "./menu.html";
import { applyBrandingState } from "@/core/entitlement";
import { renderStatementPanel } from '@/views/statement/statementPanel';

import FilterButtons from "./FilterButtons";
import ContentButtons from "./ContentButtons";
import ToolButtons from "../../enum/TOOL_PRESETS";
import ProfileButtons from "./ProfileButtons";
import renderButtons from "./renderButtons";
import {
  filterByModule,
  CONTENT_KEY_MAP,
  COLOR_KEY_MAP,
  NAV_KEY_MAP,
  PROFILE_KEY_MAP,
  shouldRemoveProfilesSection,
} from "./moduleFilter";
import adjustFontSize from "../../tools/adjustFontSize";
import renderTools from "./renderTools";
import reset from "./reset";
import translateWidget from "./translateWidget";
import { t } from "@/i18n/translate";


import css from "./menu.css";
import enableContrast from "@/tools/enableContrast";
import { pluginConfig } from "@/globals/pluginConfig";
import {
  userSettings,
  setUserStateSettings,
  saveUserSettings,
} from "@/globals/userSettings";
import { changeLanguage } from "@/i18n/changeLanguage";
import toggleMenu from "./toggleMenu";
import { getWidgetEl } from "../widget/renderButton";
import enableProfile from "@/tools/enableProfile";
import disableContrast from "@/tools/disableContrast";
import disableProfile from "@/tools/disableProfile";
import selectButtons from "@/tools/selectButtons";
import { renderLanguageDropdown } from './renderLanguageDropdown';
import { getCurrentDevice } from "@/utils/device";
import trapFocus from "@/utils/trapFocus";
import { getShortcutLabel } from '@/utils/getShortcutLabel';

/**
 * Announces changes to screen readers
 * @param message - The message to announce
 */
function announceToScreenReader(message: string): void {
  const announcements = document.getElementById('cya11y-menu-announcements');
  if (announcements) {
    announcements.textContent = message;
    // Clear the message after a short delay
    setTimeout(() => {
      announcements.textContent = '';
    }, 1000);
  }
}

/**
 * Updates ARIA pressed state for buttons
 * @param button - The button element
 * @param isPressed - Whether the button is pressed
 */
function updateButtonAriaState(button: HTMLElement, isPressed: boolean): void {
  button.setAttribute('aria-pressed', isPressed.toString());
  if (isPressed) {
    button.classList.add("cya11y-selected");
  } else {
    button.classList.remove("cya11y-selected");
  }
}

/**
 * Handles keyboard navigation for menu items
 * @param event - Keyboard event
 * @param currentButton - Current button element
 */
function handleMenuKeyboardNavigation(event: KeyboardEvent, currentButton: HTMLElement): void {
  const buttons = Array.from(currentButton.parentElement?.querySelectorAll('.cya11y-btn') || []) as HTMLElement[];
  const currentIndex = buttons.indexOf(currentButton);
  
  switch (event.key) {
    case 'ArrowRight':
    case 'ArrowDown':
      event.preventDefault();
      const nextIndex = (currentIndex + 1) % buttons.length;
      buttons[nextIndex]?.focus();
      break;
    case 'ArrowLeft':
    case 'ArrowUp':
      event.preventDefault();
      const prevIndex = currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
      buttons[prevIndex]?.focus();
      break;
    case 'Home':
      event.preventDefault();
      buttons[0]?.focus();
      break;
    case 'End':
      event.preventDefault();
      buttons[buttons.length - 1]?.focus();
      break;
  }
}

/**
 * Replaces the menu content with the accessibility statement panel.
 * Saves the original DOM nodes (preserving their event listeners) into a
 * DocumentFragment and restores them when the back button is clicked.
 */
function showStatementPanel($container: HTMLElement, $menu: HTMLElement): void {
  const $menuContent = $menu.querySelector('.cya11y-menu-content') as HTMLElement;
  if (!$menuContent) return;

  // Footer link stays clickable while the panel is open — don't stack panels.
  if ($menuContent.querySelector('.cya11y-statement-view')) return;

  // Move original children into a fragment — keeps all event listeners intact
  const fragment = document.createDocumentFragment();
  while ($menuContent.firstChild) {
    fragment.appendChild($menuContent.firstChild);
  }

  const $statementWrapper = renderStatementPanel(() => {
    // Remove statement view
    $menuContent.innerHTML = '';
    // Re-attach original children with their event listeners
    $menuContent.appendChild(fragment);
    $menuContent.scrollTop = 0;
  });

  $menuContent.appendChild($statementWrapper);
  $menuContent.scrollTop = 0;
}

export default function renderMenu() {
  const device = getCurrentDevice();
  const $container: HTMLElement = document.createElement("div");
  $container.innerHTML = `<style id="cya11y-menu-css">${css}</style>` + template;
  const $menu = $container.querySelector(".cya11y-menu") as HTMLElement;
  
  // Position menu based on widget position
  const currentPosition = pluginConfig?.position[device];
  if (currentPosition) {
    // Set data-position attribute for CSS positioning
    $menu.setAttribute('data-position', currentPosition);
    
    if (currentPosition.includes("right")) {
      $menu.style.right = "0px";
      $menu.style.left = "auto";
    } else if (currentPosition.includes("left")) {
      $menu.style.left = "0px";
      $menu.style.right = "auto";
    }
  }

  const contentElement = $menu.querySelector(".content");
  const toolsElement = $menu.querySelector(".tools");
  const contrastElement = $menu.querySelector(".contrast");
  const profilesElement = $menu.querySelector(".profiles");

  const visibleContent = filterByModule(ContentButtons, pluginConfig.modules?.content, CONTENT_KEY_MAP);
  const visibleToolsRaw = filterByModule(ToolButtons as any, pluginConfig.modules?.navigation, NAV_KEY_MAP);
  // Additionally drop page-read / mute-sounds if their top-level admin module is disabled
  const visibleTools = visibleToolsRaw.filter((btn: { key: string }) => {
    if (btn.key === 'page-read'   && pluginConfig.modules?.pageRead?.enabled   === false) return false;
    if (btn.key === 'mute-sounds' && pluginConfig.modules?.muteSounds?.enabled === false) return false;
    return true;
  });
  const visibleFilters = filterByModule(FilterButtons, pluginConfig.modules?.color, COLOR_KEY_MAP);

  // Section-level `enabled` sits beside the per-profile keys, so the object
  // doesn't structurally satisfy Record<string, {enabled}> — the cast is safe
  // because filterByModule only looks up keys present in PROFILE_KEY_MAP.
  const visibleProfiles = filterByModule(
    ProfileButtons,
    pluginConfig.modules?.profiles as unknown as Record<string, { enabled: boolean }>,
    PROFILE_KEY_MAP
  );

  if (contentElement) {
    if (visibleContent.length > 0) {
      contentElement.innerHTML = renderButtons(visibleContent);
    } else {
      (contentElement.closest('section') || contentElement.parentElement)?.remove();
    }
  }
  if (toolsElement) {
    if (visibleTools.length > 0) {
      toolsElement.innerHTML = renderButtons(visibleTools, "cya11y-tools");
    } else {
      (toolsElement.closest('section') || toolsElement.parentElement)?.remove();
    }
  }
  if (contrastElement) {
    if (visibleFilters.length > 0) {
      contrastElement.innerHTML = renderButtons(visibleFilters, "cya11y-filter");
    } else {
      (contrastElement.closest('section') || contrastElement.parentElement)?.remove();
    }
  }
  if (profilesElement) {
    profilesElement.innerHTML = renderButtons(
      visibleProfiles,
      "cya11y-profile"
    );
  }
  
  const $profileCard = $menu.querySelector(".cya11y-profile-card") as HTMLElement;
  const $profileTitleContainer = $menu.querySelector(".cya11y-profile-title-container") as HTMLElement;
  const $profileTitle = $menu.querySelector(".cya11y-profile-title") as HTMLElement;
  const $profileItems = $menu.querySelector(".cya11y-profile-items") as HTMLElement;

  // Hide profile items by default
  if ($profileItems) {
    $profileItems.style.display = 'none';
  }

  if ($profileTitleContainer && $profileTitle) {
    $profileTitleContainer.addEventListener("click", () => {
      const $profileItems = $menu.querySelector(".cya11y-profile-items") as HTMLElement;
      const isExpanded = $profileItems.style.display !== 'none';

      // Toggle visibility of profile items
      $profileItems.style.display = isExpanded ? 'none' : 'grid';

      // Update ARIA state
      $profileTitle.setAttribute('aria-expanded', (!isExpanded).toString());
      $profileCard.querySelector(".cya11y-arrow")?.classList.toggle("cya11y-arrow-rotate");

      // Announce to screen reader
      announceToScreenReader(isExpanded ? 'Accessibility profiles collapsed' : 'Accessibility profiles expanded');
    });
  }
  
  // Remove the whole profiles card — title/select control included — when the
  // admin section flag is explicitly disabled or every profile is disabled.
  // Default (missing keys) keeps everything visible.
  // Unlike the inline empty-checks above, profiles also has a section-level
  // flag, so the decision lives in shouldRemoveProfilesSection.
  if (shouldRemoveProfilesSection(pluginConfig.modules?.profiles, visibleProfiles.length)) {
    $profileCard.remove();
  }
  
  // *** States UI Rendering ***
  const states = userSettings?.states;

  const fontSize = Number(states?.fontSize) || 1;
  if (fontSize != 1) {
    const amountElement = $menu.querySelector(".cya11y-amount");
    if (amountElement) {
      amountElement.innerHTML = `${(fontSize * 100).toFixed(0)}%`;
    }
  }

  selectButtons($menu);

  // Restore text-align cycler visual state (icon + progress bar + label)
  const $textAlignBtn = $menu.querySelector('.cya11y-text-align') as HTMLElement | null;
  if ($textAlignBtn) {
    const ta = (states?.['text-align'] || false) as 'left' | 'right' | 'center' | false;
    $textAlignBtn.dataset.state = (ta || 'off') as string;
    const taLabelKey = ta === 'left' ? 'Align Left'
                     : ta === 'center' ? 'Align Center'
                     : ta === 'right' ? 'Align Right'
                     : 'Text Align';
    const $taLabel = $textAlignBtn.querySelector('.cya11y-translate') as HTMLElement | null;
    // Keep the canonical English key in data-translate (on both the label span
    // and the button) so translateWidget re-translates them on a language
    // switch. Without it the label/title freeze in whatever language was active
    // when the alignment was last set.
    if ($taLabel) {
      $taLabel.setAttribute('data-translate', taLabelKey);
      $taLabel.textContent = t(taLabelKey);
    }
    $textAlignBtn.setAttribute('data-translate', taLabelKey);
    $textAlignBtn.setAttribute('title', t(taLabelKey));
  }

  renderLanguageDropdown($menu);

  // Handle accessibility statement link / in-widget panel
  const $statementLink = $container.querySelector('.cya11y-accessibility-statement-link') as HTMLAnchorElement;
  const statementModule = pluginConfig.modules.statement;

  if ($statementLink) {
    if (statementModule.displayInWidget && statementModule.formData?.companyName) {
      // In-widget panel mode: acts as a button, no external-link icon.
      $statementLink.removeAttribute('href');
      $statementLink.setAttribute('role', 'button');
      $statementLink.setAttribute('tabindex', '0');
      $statementLink.setAttribute('aria-label', 'Accessibility statement');
      $statementLink.classList.remove('cya11y-statement-external');
      $statementLink.style.display = 'inline-flex';
      $statementLink.style.cursor = 'pointer';

      const openStatement = (e: Event) => {
        e.preventDefault();
        showStatementPanel($container, $menu);
      };
      $statementLink.addEventListener('click', openStatement);
      $statementLink.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') openStatement(e);
      });
    } else if (statementModule.enabled) {
      // External-URL mode: real link that opens in a new tab, show the icon.
      $statementLink.href = statementModule.url || '#';
      $statementLink.classList.add('cya11y-statement-external');
      $statementLink.setAttribute('aria-label', 'View accessibility statement (opens in new window)');
      $statementLink.style.display = 'inline-flex';
      // Space key doesn't activate <a> natively — add it for consistency
      $statementLink.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === ' ') { e.preventDefault(); $statementLink.click(); }
      });
    } else {
      $statementLink.style.display = 'none';
    }
  }

  if (pluginConfig.keyboard?.enabled && pluginConfig.keyboard.shortcut) {
    const $title = $menu.querySelector('.cya11y-menu-title') as HTMLElement;
    if ($title) {
      const $shortcut = document.createElement('span');
      $shortcut.className = 'cya11y-menu-title-shortcut';
      $shortcut.setAttribute('aria-hidden', 'true');
      $shortcut.textContent = ` (${getShortcutLabel(pluginConfig.keyboard.shortcut)})`;
      $title.appendChild($shortcut);
    }
  }

  // *** Utils ***
  $container
    .querySelectorAll(".cya11y-menu-close, .cya11y-overlay")
    .forEach((el) => el.addEventListener("click", toggleMenu));

  $container
    .querySelectorAll(".cya11y-menu-reset")
    .forEach((el) => el.addEventListener("click", () => {
      reset($menu);
      announceToScreenReader('All accessibility settings have been reset to default');
    }));

  // *** Controls ***
  $menu.querySelectorAll(".cya11y-plus, .cya11y-minus").forEach((el) => {
    const element = el as HTMLElement;
    element.addEventListener("click", () => {
      const difference = 0.1;

      let fontSize = userSettings?.states?.fontSize || 1;
      if (element.classList.contains("cya11y-minus")) {
        fontSize -= difference;
      } else {
        fontSize += difference;
      }

      fontSize = Math.max(fontSize, 0.1);
      fontSize = Math.min(fontSize, 4);
      fontSize = Number(fontSize.toFixed(4));

      const amountElement = $container.querySelector(".cya11y-amount") as HTMLElement;
      if (amountElement) {
        amountElement.textContent = `${(fontSize * 100).toFixed(0)}%`;
      }

      adjustFontSize(fontSize);
      userSettings.states.fontSize = fontSize;

      saveUserSettings();
      
      // Announce font size change
      announceToScreenReader(`Font size set to ${(fontSize * 100).toFixed(0)}%`);
    });
  });

  $menu.querySelectorAll(".cya11y-btn").forEach((el) => {
    const element = el as HTMLElement;
    
    // Add keyboard navigation
    element.addEventListener("keydown", (event) => {
      handleMenuKeyboardNavigation(event, element);
    });
    
    element.addEventListener("click", () => {
        const key = element.dataset.key;
        if (!key) return; // Early return if key is undefined

        const isSelected = !element.classList.contains("cya11y-selected");

      // --- Text Align (cycler) ---
        if (key === 'text-align') {
            const cycle = [false, 'left', 'center', 'right'] as const;
            const current = (userSettings.states['text-align'] || false) as typeof cycle[number];
            const next = cycle[(cycle.indexOf(current) + 1) % cycle.length];
            userSettings.states['text-align'] = next;
            element.dataset.state = (next || 'off') as string;

            const labelKey = next === 'left' ? 'Align Left'
                           : next === 'center' ? 'Align Center'
                           : next === 'right' ? 'Align Right'
                           : 'Text Align';
            const labelEl = element.querySelector('.cya11y-translate') as HTMLElement | null;
            // Store the canonical English key so a later language switch
            // re-translates label + title (translateWidget keys off data-translate).
            if (labelEl) {
                labelEl.setAttribute('data-translate', labelKey);
                labelEl.textContent = t(labelKey);
            }
            element.setAttribute('data-translate', labelKey);
            element.setAttribute('title', t(labelKey));

            if (next) {
                element.classList.add('cya11y-selected');
                updateButtonAriaState(element, true);
            } else {
                element.classList.remove('cya11y-selected');
                updateButtonAriaState(element, false);
            }
            renderTools();
            saveUserSettings();
            announceToScreenReader(next ? `Text alignment: ${next}` : 'Text alignment cleared');
            return;
        }

      // --- Contrast ---
        if (element.classList.contains("cya11y-filter")) {
            $menu
            .querySelectorAll(".cya11y-filter")
            .forEach((el) => {
              const filterElement = el as HTMLElement;
              updateButtonAriaState(filterElement, false);
            });

            if (isSelected) {
                updateButtonAriaState(element, true);
            }
        
            if (userSettings.states.contrast === key) {
                userSettings.states.contrast = false;
                saveUserSettings();
                disableContrast();
                announceToScreenReader('Contrast filter disabled');
                return;
            }
            userSettings.states.contrast = isSelected ? key : false;
            enableContrast(userSettings.states.contrast);
            saveUserSettings();
            renderTools();
            updateButtonAriaState(element, isSelected);

            // Announce contrast change
            const contrastLabel = element.querySelector('.cya11y-translate')?.textContent || key;
            announceToScreenReader(`${contrastLabel} contrast enabled`);

            return;
        }

      // --- Profiles ---
      if (element.classList.contains("cya11y-profile")) {
        $menu
          .querySelectorAll(".cya11y-profile")
          .forEach((el) => {
            const profileElement = el as HTMLElement;
            updateButtonAriaState(profileElement, false);
          });

        if (isSelected) {
          updateButtonAriaState(element, true);
        }

        if(userSettings.states[key]){
            userSettings.states[key] = false;
            saveUserSettings();
            disableProfile(key, $menu);
            announceToScreenReader(`${key} profile disabled`);
            return;
        }
        Object.keys(userSettings.states).forEach((stateKey) => {
          if(stateKey.includes('profile')){
            userSettings.states[stateKey] = false;
            saveUserSettings();
            disableProfile(stateKey, $menu);
          }
        });
        saveUserSettings();
        userSettings.states[key]= true;
        enableProfile(key, $menu);
        saveUserSettings();
        renderTools();
        updateButtonAriaState(element, isSelected);
        
        // Announce profile change
        const profileLabel = element.querySelector('.cya11y-translate')?.textContent || key;
        announceToScreenReader(`${profileLabel} profile enabled`);
      }

      updateButtonAriaState(element, isSelected);
      userSettings.states[key] = isSelected;
      renderTools();

      saveUserSettings();
      
      // Announce general setting change
      const settingLabel = element.querySelector('.cya11y-translate')?.textContent || key;
      announceToScreenReader(`${settingLabel} ${isSelected ? 'enabled' : 'disabled'}`);
    });
  });

  // Space key activation for all footer/branding links (native <a> only handles Enter)
  // statement link has its own mode-specific handlers (renderMenu above)
  $container.querySelectorAll('.cya11y-footer a:not(.cya11y-accessibility-statement-link)').forEach((el) => {
    el.addEventListener('keydown', (e: Event) => {
      if ((e as KeyboardEvent).key === ' ') {
        e.preventDefault();
        (el as HTMLElement).click();
      }
    });
  });

  const $widget = getWidgetEl();
  if ($widget) {
    $widget.appendChild($container);
  }

  // Pro: hide branding when configured and entitled (no-op on free builds).
  // Must run after $container is attached above — applyBrandingState() queries
  // the live DOM (#cya11y-container → shadowRoot → .cya11y-footer), which finds
  // nothing while $container is still detached.
  applyBrandingState();

  trapFocus($menu);

  return $menu;
}

